import type { CaseStudy } from './types'

export const telescope: CaseStudy = {
  id: 'Telescope',
  card: {
    id: 'Telescope',
    href: '/Telescope',
    image: '/assets/tel/dashboard.webp',
    imageAlt: 'Telescope',
    title: 'Telescope',
    miniDescription: 'AI resume builder & job-application tracker.',
    caseNum: '02 - Careers - SaaS',
    lead: 'An AI resume builder and job-application tracker - tailor a resume to each role, score the match, and keep every application moving. In active development.',
    tags: [
      { label: 'TanStack Start' },
      { label: 'AI analysis', key: true },
      { label: 'Cloudflare D1' },
      { label: 'better-auth' },
      { label: 'PDF export' },
    ],
    outcomes: [
      'Import from PDF / DOCX, tailored resumes per role',
      'ATS + job-description match scoring',
      'Drag-to-move application pipeline',
    ],
  },
  imageSizes: {
    '/assets/tel/board.webp': { width: 1280, height: 720 },
    '/assets/tel/companies.webp': { width: 1280, height: 720 },
    '/assets/tel/dashboard.webp': { width: 1280, height: 720 },
    '/assets/tel/resumes.webp': { width: 1280, height: 720 },
  },
  content: {
    metaTitle: 'Telescope — Case Study · ThroughPilot',
    metaDescription:
      'How ThroughPilot is building Telescope: an AI resume builder and job-application tracker with matching, resume tailoring, and a pipeline board.',
    path: '/Telescope',
    ogImage: '/assets/tel/dashboard.webp',
    kicker: 'Case study · Careers · SaaS',
    title: (
      <>
        <em>Telescope</em>
      </>
    ),
    liveHref: 'https://telescope.throughpilot.com',
    lede: 'An AI résumé builder and job-application tracker — one calm place to tailor a résumé to each role, see how well it matches, and keep every application moving.',
    meta: [
      { label: 'Role', value: 'Product Design & Full-stack' },
      {
        label: 'Status',
        value: (
          <>
            <span className="dot" />
            In active development
          </>
        ),
      },
      { label: 'Service', value: 'Launchpad — MVP → Production' },
    ],
    chips: [
      { label: 'TanStack Start', key: true },
      { label: 'React 19' },
      { label: 'Cloudflare D1 + Workers', key: true },
      { label: 'better-auth' },
      { label: 'OpenAI', key: true },
      { label: 'R2 storage' },
      { label: 'Puppeteer (PDF)' },
    ],
    heroImage: {
      src: '/assets/tel/dashboard.webp',
      alt: 'Telescope dashboard — Good morning briefing with response rate and pipeline',
    },
    overview: {
      label: 'The idea',
      title: (
        <>
          The job hunt, <em>without</em> the chaos.
        </>
      ),
      body: (
        <>
          <p>
            Looking for a job means juggling a dozen résumé versions, a
            spreadsheet of applications, and a vague sense of which threads are
            going cold. Telescope pulls all of it into one product that actually
            helps you decide what to do next.
          </p>
          <p>
            You start by <b>dropping in an existing résumé</b> — PDF, DOCX or TXT
            — and the app parses it into a structured, editable document. From
            there you tailor copies to specific roles, let the AI{' '}
            <b>score the match and flag what's missing</b>, and track every
            application on a board that always shows what needs attention today.
          </p>
          <p>
            It's <b>in active development</b> — we're designing and building it
            end to end, shipping the core loop first: import, tailor, analyse,
            track.
          </p>
        </>
      ),
    },
    highlights: {
      title: 'What it does.',
      features: [
        {
          num: '01 — Résumé library',
          title: 'Master résumés, tailored per role.',
          media: {
            src: '/assets/tel/resumes.webp',
            alt: 'Telescope résumé library — master résumés and tailored copies with match scores',
          },
          body: (
            <p>
              Keep a couple of master résumés as your source of truth, then spin
              off tailored copies for each job. Every tailored version carries its
              own match score, so you always know which one to send.
            </p>
          ),
          points: [
            'Import once from PDF / DOCX / TXT — parsed into structured data',
            'Three templates: Classic, Minimal, Modern',
            'Per-role copies that stay linked to the master',
          ],
        },
        {
          num: '02 — AI analysis',
          title: 'Know your match before you apply.',
          flip: true,
          media: (
            <div className="acard">
              <div className="acard-head">
                <div className="ring">
                  <span>87</span>
                </div>
                <div className="ht">
                  <div className="b">ATS score</div>
                  <div className="m">Match · Anthropic — 91%</div>
                  <div className="s">Strong match</div>
                </div>
              </div>
              <div className="arow">
                <div className="al">Missing keywords</div>
                <div className="kw">
                  <span className="miss">design systems</span>
                  <span className="miss">0→1 product</span>
                  <span className="miss">user research</span>
                  <span className="miss">A/B testing</span>
                </div>
              </div>
              <div className="arow">
                <div className="al">Suggested fixes</div>
                <ul className="fixes">
                  <li>Lead your Stripe bullet with measurable impact</li>
                  <li>Name the design-system work explicitly</li>
                  <li>Mirror the JD's "0→1" language in your summary</li>
                </ul>
              </div>
            </div>
          ),
          body: (
            <p>
              Paste a job description and the AI scores your résumé against it —
              an ATS readiness score, a match score, the keywords you're missing,
              skill gaps, and concrete fixes. No more guessing whether you're in
              range.
            </p>
          ),
          points: [
            'ATS score + job-description match score',
            'Missing keywords & skill-gap detection',
            'Specific, rewrite-ready suggestions',
          ],
        },
        {
          num: '03 — Application tracker',
          title: 'Every role, where it really stands.',
          media: {
            src: '/assets/tel/board.webp',
            alt: 'Telescope job board — Kanban from Saved to Offer',
          },
          body: (
            <p>
              A board that moves with your search: Saved → Applied → Interview →
              Offer → Closed. Drag a card to advance it, attach the résumé you
              sent, and see deadlines, salary bands and what's gone quiet at a
              glance.
            </p>
          ),
          points: [
            'Drag-to-move pipeline with five clear stages',
            'Deadlines, salary bands & "gone quiet" nudges',
            'The résumé you sent, attached to each role',
          ],
        },
        {
          num: '04 — Company memory',
          title: '"Have we met before?"',
          flip: true,
          media: {
            src: '/assets/tel/companies.webp',
            alt: 'Telescope companies — application history per company',
          },
          body: (
            <p>
              Search any company and Telescope shows whether — and when — you've
              applied before, with the full history per role. No more re-pitching
              a team that passed last year, or forgetting you're already in their
              pipeline.
            </p>
          ),
          points: [
            'Per-company application history & status',
            'Industry & size at a glance',
            'Surfaces past outcomes before you re-apply',
          ],
        },
      ],
    },
    hood: {
      title: 'Edge-native and type-safe.',
      cards: [
        {
          t: 'Framework',
          v: 'TanStack Start',
          d: 'React 19, file-based routing, server functions.',
        },
        {
          t: 'Data',
          v: 'Cloudflare D1',
          d: 'SQLite at the edge, with Drizzle migrations.',
        },
        {
          t: 'Auth',
          v: 'better-auth',
          d: <>Sessions &amp; sign-in, wired into every route.</>,
        },
        {
          t: 'AI',
          v: 'OpenAI',
          d: <>Résumé parsing, analysis &amp; match scoring.</>,
        },
        {
          t: 'Files',
          v: 'R2 + pdf/docx parse',
          d: 'Uploads stored in R2; text extracted server-side.',
        },
        {
          t: 'Export',
          v: 'Puppeteer',
          d: 'Pixel-accurate PDF export of any template.',
        },
      ],
      note: (
        <>
          Built on Cloudflare's edge so it stays fast and cheap to run, with every
          résumé, job and analysis result modelled in a type-safe schema. The AI
          never blocks the UI — analysis runs on demand and{' '}
          <b>degrades gracefully</b> if a model call fails.
        </>
      ),
    },
    outcome: {
      label: "Where it's at",
      title: 'The core loop, shipping now.',
      metrics: [
        { n: 'Import → tailor', l: 'PDF · DOCX · TXT, parsed by AI', sm: true },
        {
          n: (
            <>
              2<em>-score</em>
            </>
          ),
          l: 'ATS readiness + JD match',
        },
        {
          n: (
            <>
              5<em>-stage</em>
            </>
          ),
          l: 'Drag-to-move application board',
        },
        { n: '3', l: 'Résumé templates, PDF export' },
      ],
    },
    prev: {
      href: '/FairyHomes',
      dir: '← Prev case',
      ttl: (
        <>
          <em>FairyHomes</em>.ae
        </>
      ),
    },
    next: {
      href: '/1Mind',
      dir: 'Next case →',
      ttl: (
        <>
          1<em>Mind.com</em>
        </>
      ),
    },
  },
}
